import { isFunction } from 'underscore';
import React, { useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { UrlOptionsParameters } from './url-options-parameters';


const UrlOptionsParametersWithCustomValidation = ({
  onChange: onChangeFromProps, updateValidation, validationProps, ...restProps
}) => {
  const { data } = validationProps;
  const onChange = useCallback((value, key, errors) => {
    const newValue = {
      ...data,
      [key]: value,
    };
    if (isFunction(onChangeFromProps)) {
      onChangeFromProps(value, key, errors);
    }
    updateValidation(errors, newValue);
  }, [data, onChangeFromProps, updateValidation]);

  return (
    <UrlOptionsParameters
      enableSelfValidation
      disableLengthConstraints
      showFinalUrlSuffix
      onChange={onChange}
      trackingUrlTemplate={data.TrackingUrlTemplate}
      finalUrlSuffix={data.FinalUrlSuffix}
      urlCustomParameters={data.UrlCustomParameters}
      {...restProps}
    />
  );
};

UrlOptionsParametersWithCustomValidation.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      TrackingUrlTemplate: PropTypes.string,
      FinalUrlSuffix: PropTypes.string,
      UrlCustomParameters: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string,
        Value: PropTypes.string,
      })),
    }),
  }).isRequired,
  onChange: PropTypes.func,
};

UrlOptionsParametersWithCustomValidation.defaultProps = {
  onChange: undefined,
};

export const ValidatedUrlOptionsParameters = withCustomValidation(UrlOptionsParametersWithCustomValidation);

const UrlOptionsParametersWithError = ({
  onChange: onChangeFromProps, updateValidation, validationProps, isTrackingUrlTemplateRequired, isWithError, ...restProps
}) => {
  const { data } = validationProps;
  const i18n = useContext(I18nContext);

  const onChange = useCallback((value, key, errors) => {
    const newValue = {
      ...data,
      [key]: value,
    };
    if (isFunction(onChangeFromProps)) {
      onChangeFromProps(value, key, errors);
    }
    updateValidation(errors, newValue);
  }, [data, onChangeFromProps, updateValidation]);

  useEffect(() => {
    if (isTrackingUrlTemplateRequired && data && !data.TrackingUrlTemplate) {
      const error = [i18n.getString(_TL_('Please add a tracking template.'))];
      updateValidation(error, data, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UrlOptionsParameters
      enableSelfValidation
      disableLengthConstraints
      showFinalUrlSuffix
      onChange={onChange}
      trackingUrlTemplate={data.TrackingUrlTemplate}
      finalUrlSuffix={data.FinalUrlSuffix}
      urlCustomParameters={data.UrlCustomParameters}
      isTrackingUrlTemplateRequired={isTrackingUrlTemplateRequired}
      isWithError={isWithError}
      {...restProps}
    />
  );
};

UrlOptionsParametersWithError.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      TrackingUrlTemplate: PropTypes.string,
      FinalUrlSuffix: PropTypes.string,
      UrlCustomParameters: PropTypes.arrayOf(PropTypes.shape({
        Key: PropTypes.string,
        Value: PropTypes.string,
      })),
    }),
  }).isRequired,
  onChange: PropTypes.func,
  isTrackingUrlTemplateRequired: PropTypes.bool.isRequired,
  isWithError: PropTypes.bool.isRequired,
};

UrlOptionsParametersWithError.defaultProps = {
  onChange: undefined,
};

export const ValidatedUrlOptionsParametersWithError = withError(withCustomValidation(UrlOptionsParametersWithError));
